import React, { useContext, useState } from 'react';
import { Box, Button, Link } from '@mui/material';
import { Colors, buttons } from '../../utils/theme';
import { IEtf, useAddEtfMutation, useUpdateEtfMutation } from '../../generated/graphql';
import { Edit } from '@mui/icons-material';
import { parseDate } from '../../utils/clientUtils';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { canUserEdit } from '../../utils/rightUtils';
import { Types } from '../../enums/TypeEnums';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import InputComponent from '../Common/Fields/InputComponent';
import DialogBox from '../Common/Layout/DialogBox';
import { LocalizationProvider } from '@mui/x-date-pickers';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';

type IProps = {
  handleClose: (saved: boolean, isNew?: boolean, etfInput?: IEtf) => void;
  etfInput: IEtf | null;
};

type IErrors = {
  launchDate: string | null;
  name: string | null;
  benchMarkName: string | null;
  provider: string | null;
};

const defaultErrorsObj: IErrors = {
  launchDate: null,
  name: null,
  benchMarkName: null,
  provider: null
};

const errorTexts = {
  launchDate: 'Launch Date is required!',
  provider: 'Provider is required!',
  name: 'Name is required!',
  benchmark: 'Benchmark Name is required!'
};

const EtfForm: React.FunctionComponent<IProps> = ({ handleClose, etfInput }: IProps) => {
  const [editable, setEditable] = useState(etfInput === null);
  const [launchDate, setlaunchDate] = useState<Date | null>(
    etfInput ? parseDate(etfInput.launch_date) : new Date()
  );
  const [provider, setProvider] = useState<string | null>(etfInput?.provider || null);
  const [name, setName] = useState<string | null>(etfInput?.name || null);
  const [benchmark, setBenchmark] = useState<string | null>(etfInput?.identifier || null);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [createEtf, createEtfResult] = useAddEtfMutation();
  const [updateEtf, updateEtfResult] = useUpdateEtfMutation();
  const { dispatch, state } = useContext(AppContext);
  const canEdit = canUserEdit(state.user, Types.ETF);

  const handleCancel = () => {
    if (editable && etfInput !== null) {
      setlaunchDate(parseDate(etfInput.launch_date));
      setProvider(etfInput.provider || null);
      setName(etfInput.name || null);
      setBenchmark(etfInput.identifier || null);
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const handleDateChange = (input: Date | null) => {
    if (input) {
      if (!Number.isNaN(input.getDate())) {
        setFormErrors({ ...formErrors, launchDate: null });
      }
    } else {
      setFormErrors({ ...formErrors, launchDate: errorTexts.launchDate });
    }
    setlaunchDate(input);
  };

  const handleProviderChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (value && value.length > 0) {
      setFormErrors({ ...formErrors, provider: null });
    } else {
      setFormErrors({ ...formErrors, provider: errorTexts.provider });
    }
    setProvider(value);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (value && value.length > 0) {
      setFormErrors({ ...formErrors, name: null });
    } else {
      setFormErrors({ ...formErrors, name: errorTexts.name });
    }
    setName(value);
  };

  const handleBenchmarkChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (value && value.length > 0) {
      setFormErrors({ ...formErrors, benchMarkName: null });
    } else {
      setFormErrors({ ...formErrors, benchMarkName: errorTexts.benchmark });
    }
    setBenchmark(value);
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      benchMarkName: null,
      launchDate: null,
      name: null,
      provider: null
    };

    if (!launchDate) {
      hasErrors = true;
      errors.launchDate = errorTexts.launchDate;
    }
    if (!benchmark || benchmark.length === 0) {
      hasErrors = true;
      errors.benchMarkName = errorTexts.benchmark;
    }
    if (!provider || provider.length === 0) {
      hasErrors = true;
      errors.provider = errorTexts.provider;
    }
    if (!name || name.length === 0) {
      hasErrors = true;
      errors.name = errorTexts.name;
    }

    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleSave(etfInput === null ? 'create' : 'update');
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    const input = {
      name: name as string,
      provider: provider as string,
      launch_date: (launchDate as Date).toString(),
      identifier: benchmark as string
    };

    if (type === 'create') {
      createEtf({
        variables: {
          input
        },
        update(_, { data }) {
          const newEtf = data?.result as IEtf;
          handleClose(true, true, newEtf);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `Etf ${newEtf.name} created`
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
    } else {
      updateEtf({
        variables: {
          id: etfInput?.id as string,
          input
        },
        update(_, { data }) {
          handleClose(true, false, data?.result as IEtf);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Etf Updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    }
  };

  return (
    <DialogBox id="etf-form" width={690} title={etfInput ? 'Etf Details' : 'Create ETF'}>
      {etfInput && (
        <Box sx={{ textAlign: 'right', marginRight: '15px', marginTop: '10px' }}>
          <Button
            id="ftse-form-edit-button"
            data-testid="ftse-form-edit-button"
            component={Link}
            onClick={() => setEditable(true)}
            disabled={editable || !canEdit}
            sx={{ ...buttons.edit, fontWeight: 'bold' }}
          >
            <Edit
              sx={{
                fontSize: 'medium',
                color: editable || !canEdit ? Colors.gray : Colors.skyBlue,
                marginRight: '3'
              }}
            />
            EDIT
          </Button>
        </Box>
      )}
      <Box sx={{ m: '42px', mt: '60px', display: 'flex', flexDirection: 'column', gap: '40px' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <EpraDatePicker
            id="etf-date-picker"
            label="ETF Launch Date"
            value={launchDate}
            format={'dd/MM/yyyy'}
            errorMsg={formErrors.launchDate}
            clearable={true}
            onChange={(d) => handleDateChange(d)}
            disabled={!editable || createEtfResult.loading || updateEtfResult.loading}
            sx={{ width: '200px' }}
          />
        </LocalizationProvider>
        <InputComponent
          id="etf-provider"
          label="ETF Provider"
          value={provider}
          onChange={handleProviderChange}
          errorMsg={formErrors.provider}
          disabled={!editable || createEtfResult.loading || updateEtfResult.loading}
        />
        <InputComponent
          id="etf-name"
          label="ETF Name"
          value={name}
          onChange={handleNameChange}
          errorMsg={formErrors.name}
          disabled={!editable || createEtfResult.loading || updateEtfResult.loading}
        />
        <InputComponent
          id="etf-benchmark"
          label="ETF Benchmark"
          value={benchmark}
          onChange={handleBenchmarkChange}
          errorMsg={formErrors.benchMarkName}
          disabled={!editable || createEtfResult.loading || updateEtfResult.loading}
        />
        <Box className="centralize" sx={{ marginTop: '20px' }}>
          <CancelButton
            id="cancel-etf-button"
            data-testid="cancel-etf-button"
            disabled={createEtfResult.loading || updateEtfResult.loading}
            onClick={handleCancel}
            text={etfInput === null || editable ? 'Cancel' : 'Close'}
          />
          <SaveButton
            id="save-etf-button"
            data-testid="save-etf-button"
            disabled={!editable || createEtfResult.loading || updateEtfResult.loading}
            onClick={handleSubmit}
            text={etfInput === null ? 'Create' : 'Save'}
          />
        </Box>
      </Box>
    </DialogBox>
  );
};

export default EtfForm;
